import React, { ReactNode } from 'react';

import styled from 'styled-components';

import { useMobileContext } from '../MobileContext';

export interface DataTableLayoutProps {
  /** Table content */
  children: ReactNode;
  /** Table title */
  title?: ReactNode;
  /** Toolbar/actions above table */
  toolbar?: ReactNode;
  /** Footer content below table */
  footer?: ReactNode;
  /** Custom className */
  className?: string;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state content */
  emptyContent?: ReactNode;
  /** Table configuration */
  tableConfig?: {
    /** Show table borders */
    showBorders?: boolean;
    /** Striped rows */
    striped?: boolean;
    /** Compact row spacing */
    compact?: boolean;
    /** Fixed header */
    stickyHeader?: boolean;
  };
  /** Mobile configuration */
  mobileConfig?: {
    /** Hide table on mobile, show cards instead */
    hideOnMobile?: boolean;
    /** Scroll horizontally on mobile */
    horizontalScroll?: boolean;
  };
}

/**
 * Standardized data table layout component with header, toolbar, and footer.
 * Replaces custom table wrapper patterns throughout the application.
 *
 * Features:
 * - Consistent table spacing and styling
 * - Toolbar and footer integration
 * - Loading and empty states
 * - Mobile-responsive behavior
 * - Sticky header support
 * - Theme integration
 *
 * @example
 * // Patient data table with toolbar
 * <DataTableLayout
 *   title="Patient List"
 *   toolbar={
 *     <ActionBarLayout align="space-between">
 *       <SearchInput placeholder="Search patients..." />
 *       <Button variant="primary">Add Patient</Button>
 *     </ActionBarLayout>
 *   }
 *   tableConfig={{ striped: true, stickyHeader: true }}
 * >
 *   <DataTable columns={columns} data={patients} />
 * </DataTableLayout>
 *
 * @example
 * // Simple file data table
 * <DataTableLayout
 *   tableConfig={{ compact: true, showBorders: true }}
 *   mobileConfig={{ horizontalScroll: true }}
 * >
 *   <Table>
 *     <thead>...</thead>
 *     <tbody>...</tbody>
 *   </Table>
 * </DataTableLayout>
 */
export const DataTableLayout: React.FC<DataTableLayoutProps> = ({
  children,
  title,
  toolbar,
  footer,
  className,
  isLoading = false,
  emptyContent,
  tableConfig = {},
  mobileConfig = {},
}) => {
  const { isMobile } = useMobileContext();

  const { showBorders = false, striped = false, compact = false, stickyHeader = false } = tableConfig;

  const { hideOnMobile = false, horizontalScroll = false } = mobileConfig;

  // Hide on mobile if configured
  if (isMobile && hideOnMobile) {
    return null;
  }

  if (isLoading) {
    return (
      <StyledDataTableLayout className={className} $isMobile={isMobile}>
        {title && <StyledTableHeader $isMobile={isMobile}>{title}</StyledTableHeader>}
        <StyledLoadingContainer>
          <div>Loading data...</div>
        </StyledLoadingContainer>
      </StyledDataTableLayout>
    );
  }

  if (!React.Children.count(children) && emptyContent) {
    return (
      <StyledDataTableLayout className={className} $isMobile={isMobile}>
        {title && <StyledTableHeader $isMobile={isMobile}>{title}</StyledTableHeader>}
        {toolbar && <StyledTableToolbar $isMobile={isMobile}>{toolbar}</StyledTableToolbar>}
        <StyledEmptyContainer>{emptyContent}</StyledEmptyContainer>
      </StyledDataTableLayout>
    );
  }

  return (
    <StyledDataTableLayout className={className} $isMobile={isMobile}>
      {/* Table Title */}
      {title && <StyledTableHeader $isMobile={isMobile}>{title}</StyledTableHeader>}

      {/* Toolbar */}
      {toolbar && <StyledTableToolbar $isMobile={isMobile}>{toolbar}</StyledTableToolbar>}

      {/* Table Container */}
      <StyledTableContainer $isMobile={isMobile} $horizontalScroll={horizontalScroll} $stickyHeader={stickyHeader} $showBorders={showBorders}>
        <StyledTableWrapper $striped={striped} $compact={compact} $showBorders={showBorders} $stickyHeader={stickyHeader}>
          {children}
        </StyledTableWrapper>
      </StyledTableContainer>

      {/* Footer */}
      {footer && <StyledTableFooter $isMobile={isMobile}>{footer}</StyledTableFooter>}
    </StyledDataTableLayout>
  );
};

DataTableLayout.displayName = 'DataTableLayout';

// Styled Components
interface StyledLayoutProps {
  $isMobile: boolean;
}

interface StyledContainerProps extends StyledLayoutProps {
  $horizontalScroll: boolean;
  $stickyHeader: boolean;
  $showBorders: boolean;
}

interface StyledWrapperProps {
  $striped: boolean;
  $compact: boolean;
  $showBorders: boolean;
  $stickyHeader: boolean;
}

const StyledDataTableLayout = styled.div<StyledLayoutProps>`
  /* Container for entire table layout */
  display: grid;
  grid-template-rows: auto auto 1fr auto;
  min-height: 0;
  background-color: ${({ theme }) => theme.colors.palette.white};
  border-radius: ${({ theme }) => theme.radii.scale[1]};
  overflow: hidden;
`;

const StyledTableHeader = styled.div<StyledLayoutProps>`
  /* Table title area */
  padding: ${({ theme, $isMobile }) => ($isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`)};

  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.grayscale[0]};

  font-size: ${({ theme }) => theme.fontSizes.heading2};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  color: ${({ theme }) => theme.colors.textPrimary};
`;

const StyledTableToolbar = styled.div<StyledLayoutProps>`
  /* Toolbar/actions area */
  padding: ${({ theme, $isMobile }) => ($isMobile ? `${theme.space.spacing10} ${theme.space.spacing10}` : `${theme.space.spacing20} ${theme.space.spacing20}`)};

  border-bottom: 1px solid ${({ theme }) => theme.colors.borderBase};
  background-color: ${({ theme }) => theme.colors.palette.white};
`;

const StyledTableContainer = styled.div<StyledContainerProps>`
  /* Scrollable table container */
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  ${({ $showBorders, theme }) =>
    $showBorders &&
    `
    border: 1px solid ${theme.colors.borderBase};
    border-radius: ${theme.radii.scale[1]};
    margin: ${theme.space.spacing10} ${theme.space.spacing20};
  `}

  /* Mobile horizontal scroll */
  ${({ $isMobile, $horizontalScroll }) =>
    $isMobile &&
    $horizontalScroll &&
    `
      overflow-x: auto;
      overflow-y: hidden;
    `}

  /* Sticky header support */
  ${({ $stickyHeader }) =>
    $stickyHeader &&
    `
    max-height: 70vh;
  `}
`;

const StyledTableWrapper = styled.div<StyledWrapperProps>``;

const StyledTableFooter = styled.div<StyledLayoutProps>``;

const StyledLoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.spacing40};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSizes.body};
`;

const StyledEmptyContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space.spacing40};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
`;
